import React, {useCallback, useEffect, useState, useMemo} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {useForm, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {exists, generateUniqueKey, objectGuard} from "../../utils/helpers.js";
import {
  COMPONENT,
  deepCopyBuilder,
  initialFieldValues,
  updateElement
} from "../../utils/builder.js";
import {
  getAncestryName,
  getLinkedComponentOptions,
  resolveDependencyMismatches
} from "./helpers.js";

// Components
import Modal from "../../components/Modal.js";
import Help from "../../components/Help.js";
import RenderChecksheet from "./RenderChecksheet.js";
import ModalExternalTemplateFields from "./ModalExternalTemplateFields.js";
import {InputSelectGroup} from "../../components/form/FormInputs.js";

// Style
import {voice} from "../../style/components/typography.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {
  FormGroup,
  FormField,
  HeadingCenter,
  ButtonFull,
  Form,
  Label,
  Error
} from "../../style/components/general.js";

const ModalApplyTemplate = ({
  visible,
  setShowModal,
  builder,
  saveBuilder,
  template,
  recursePreview,
  setUnits
}) => {
  const isMounted = useMountedState();

  const defaultComponent = useMemo(() => ({componentToApply: [{}]}), []);

  const schema = yup.object().shape({
    componentToApply: yup.array().of(
      yup.object().shape({
        value: yup.string().required("Please select a component to apply template.")
      })
    )
  });

  const form = useForm({
    defaultValues: defaultComponent,
    resolver: yupResolver(schema)
  });
  const {handleSubmit, reset, watch} = form;

  const [externalIdx, setExternalIdx] = useState(null);
  const [associations, setAssociations] = useState({});
  const [associationComplete, setAssociationComplete] = useState(false);
  const [hasFields, setHasFields] = useState(false);

  const componentToApply = watch("componentToApply");

  const formatOptions = useCallback(
    targetFields =>
      targetFields.map(field => {
        if (field.options && field.options.length > 0)
          return {
            ...field,
            options: field.options.map(option => ({
              option: option.option || option.value || objectGuard(option)
            })),
            toggle: true
          };

        return {...field, toggle: true};
      }),
    []
  );

  const formatSelected = useCallback(() => {
    const templateFields =
      template.fields && template.fields.length > 0
        ? formatOptions(template.fields)
        : [initialFieldValues];

    const formatted = {
      id: template.id,
      label: template.label || "",
      fields: templateFields
    };

    if (template.help?.length > 0) formatted.help = template.help;

    return formatted;
  }, [formatOptions, template]);

  const templateBuilder = useMemo(() => {
    const formatted = formatSelected(template);
    const fields = [...formatted.fields];
    delete formatted.fields;
    const children = [];
    const nameMap = {};
    const fieldObjs = Object.fromEntries(
      fields.map((field, i) => {
        const name = generateUniqueKey(field.label);
        nameMap[field.name || `field${i}`] = name;
        children.push(name);
        return [
          name,
          {
            ...field,
            parentName: "component",
            name,
            element: "field"
          }
        ];
      })
    );
    const updated = {
      byId: {
        ...fieldObjs,
        component: {
          ...formatted,
          element: "component",
          children,
          label: formatted.label,
          name: "component",
          toggle: true
        }
      },
      allIds: ["component"]
    };
    resolveDependencyMismatches(updated, nameMap);
    return updated;
  }, [formatSelected, template]);

  // Handle components with fields
  useEffect(() => {
    if (isMounted())
      setHasFields(
        componentToApply?.some(
          component =>
            builder.byId[component.value]?.children?.length > 0 &&
            builder.byId[component.value]?.children.some(
              child =>
                builder.byId[child]?.toggle &&
                !builder.byId[child]?.deleted &&
                !builder.byId[child]?.shared
            )
        ) || false
      );
  }, [isMounted, componentToApply, builder]);

  const handleApplyTemplate = useCallback(() => {
    if (template.external && Object.keys(template.external).length > 0 && !associationComplete) {
      setExternalIdx(0);
      return;
    }

    let updated = deepCopyBuilder(builder);

    const components = [...componentToApply];
    const previousById = {...builder.byId};
    const newUnits = [];

    const appliedTemplates = {
      [template.id]: components.map(({value}) => value)
    };

    let formattedAssociations = {};
    if (Object.keys(associations).length > 0)
      formattedAssociations = Object.fromEntries(
        Object.entries(associations).map(([key, {value}]) => [key, value])
      );

    components.forEach(({value: target}, idx) => {
      if (idx === 0)
        template.fields.forEach(field => {
          if (!field.units) return;

          const normalized = field.units.replace("Other: ", "");

          if (!newUnits.includes(normalized)) {
            setUnits(prev => [...prev, normalized].sort());
            newUnits.push(normalized);
          }
        });

      const nameMap = {...formattedAssociations};
      const update = {
        element: COMPONENT,
        toggle: true,
        name: target,
        parentName: builder.byId[target].parentName,
        fields: template.fields
          .filter(({type}) => type !== "weather")
          .map((field, i) => {
            let foundHelp;
            if (template.help) {
              foundHelp = template.help.find(({key}) => key === field.label);
            }

            return {
              ...field,
              name: field.name || `field${i}`,
              generates: [],
              formula: field.formula ? [...field.formula.map(part => ({...part}))] : null,
              units: field.units ? field.units.replace("Other: ", "") : null,
              condition: field.condition
                ? {...field.condition, list: field.condition.list.map(item => ({...item}))}
                : null,
              help: foundHelp?.value
            };
          }),
        templateId: template.id
      };

      if (template?.help?.length > 0) update.help = template.help;

      ({builder: updated} = updateElement(updated, update, true, nameMap));
      resolveDependencyMismatches(updated, {...nameMap, component: target}, true);

      updated.byId = Object.fromEntries(
        Object.entries(updated.byId).filter(([name]) => !name.match("ext[0-9]*"))
      );

      if (update.help)
        update.help = update.help.map(item => ({
          ...item,
          id: item.id in nameMap ? nameMap[item.id] : ""
        }));

      if (updated.byId[update.parentName].preview)
        updated.byId = recursePreview(update.name, updated.byId);
    });

    saveBuilder(updated, newUnits, appliedTemplates, previousById);
    setShowModal(false);
    reset({...defaultComponent});
  }, [
    associationComplete,
    associations,
    builder,
    componentToApply,
    defaultComponent,
    recursePreview,
    reset,
    saveBuilder,
    setShowModal,
    setUnits,
    template
  ]);

  const renderComponentDropdown = () => {
    if (!builder) return null;

    const linkedComponents = getLinkedComponentOptions(builder, "", {
      predicate: ({children}) =>
        !children.length ||
        children.every(
          child =>
            builder.byId[child]?.deleted ||
            !builder.byId[child]?.toggle ||
            builder.byId[child]?.shared
        )
    }).map(({name}) => ({value: name, label: getAncestryName(builder.byId[name], builder)}));

    return (
      <FormField>
        <InputSelectGroup
          name="componentToApply"
          placeholder="Apply to Component..."
          options={linkedComponents}
          testId="applyTemplate.componentList"
        />
        {hasFields && (
          <ErrorText>
            The selected component already has fields. Templates can only be applied to an empty
            component &ndash; remove its existing fields first, or add a new empty component to
            apply this template to.
          </ErrorText>
        )}
        {linkedComponents.length === 0 && (
          <ErrorText>
            Add an empty component to the checksheet first, then apply this template to it.
          </ErrorText>
        )}
      </FormField>
    );
  };

  // triggered when final page is passed on the field replacement process
  useEffect(() => {
    if (associationComplete) handleApplyTemplate();
  }, [handleApplyTemplate, associationComplete]);

  if (exists(externalIdx) && externalIdx < Object.keys(template.external).length)
    return (
      <ModalExternalTemplateFields
        external={template.external}
        externalIdx={externalIdx}
        setExternalIdx={setExternalIdx}
        associations={associations}
        setAssociations={setAssociations}
        templateFields={template.fields}
        byId={builder.byId}
        componentNames={componentToApply.map(({value}) => value)}
        templateName={template.label}
        setComplete={setAssociationComplete}
        setVisible={setShowModal}
      />
    );

  return (
    <Modal testId="facility.addComponent" visible={visible} setVisible={setShowModal}>
      <ModalTitle>Apply Component Template</ModalTitle>
      <FormProvider {...form}>
        <Form>
          <FormGroup>
            <Label>
              <strong>Template:</strong> {template.label}
            </Label>
          </FormGroup>

          <ApplyLabelRow>
            <ApplyLabel>Select existing component to apply {template.label}</ApplyLabel>
            <Help title="Applying a template">
              A template can only be applied to an empty component &ndash; one with no fields of
              its own yet. Add an empty component to the checksheet first, then select it here.
              Existing fields on a component are not replaced automatically.
            </Help>
          </ApplyLabelRow>
          {renderComponentDropdown()}
        </Form>
      </FormProvider>

      <ComponentPreview>
        <Label bold>Preview</Label>

        <RenderChecksheet task={{builder: templateBuilder}} readOnly hideMeta />
      </ComponentPreview>
      {template.external && Object.keys(template.external).length > 0 && (
        <Error>
          This template requires relinking of fields that were originally located outside the
          component.
        </Error>
      )}
      <Submit type="button" onClick={handleSubmit(handleApplyTemplate)} disabled={hasFields}>
        {template.external && Object.keys(template.external).length > 0
          ? "Continue"
          : "Apply Template"}
      </Submit>
    </Modal>
  );
};

ModalApplyTemplate.propTypes = {
  visible: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  template: PropTypes.objectOf(PropTypes.any),
  saveBuilder: PropTypes.func.isRequired,
  recursePreview: PropTypes.func.isRequired,
  setUnits: PropTypes.func.isRequired
};

// Style Overrides
const ModalTitle = styled(HeadingCenter)`
  margin: ${pad}px 0;
  color: ${({theme}) => theme.component};
`;

const ComponentPreview = styled(FormGroup)`
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: ${pad}px;
`;

const Submit = styled(ButtonFull)`
  margin-top: ${pad}px;
  margin-bottom: ${pad}px;
`;

const ErrorText = styled.p`
  ${voice.quiet};
  padding: 0 ${pad / 2}px;
  color: ${({theme}) => theme.error};
  margin-top: ${pad}px;
`;

const ApplyLabel = styled(Label)`
  font-weight: bold;
  margin-bottom: 0;
`;

const ApplyLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${pad / 4}px;
  margin-bottom: -${pad * 1.5}px;
`;

export default ModalApplyTemplate;
